import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Plus, Bookmark, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { processRows } from '@/components/operations/processRows';
import { useSavedViews, SavedView } from '@/components/operations/savedViews';
import { OperationCard } from '@/components/operations/OperationCard';
import { CompactOperationsTable } from '@/components/operations/CompactOperationsTable';
import { CalendarView } from '@/components/operations/CalendarView';
import { ChartView } from '@/components/operations/ChartView';
import { ENTITIES, ENTITY_LIST, EntityConfig, EntityKey } from '@/components/entities/registry';
import { DashboardToolbar, EntitySavedViews, ViewPick } from './DashboardToolbar';
import { ColumnEditor } from './ColumnEditor';
import { COLOR_PRESETS } from '@/components/operations/formats';
import { ColorPreset } from '@/components/operations/types';
import type { ChartBackground } from './dashboardViews';
import {
  DashboardColumn,
  DEFAULT_GRID_H,
  DEFAULT_GRID_W,
  GRID_COLS,
  GRID_GAP_PX,
  GRID_ROW_PX,
  columnsEqual,
  migrateColumnsToGrid,
  useDashboardViews,
} from './dashboardViews';

const COLUMNS_STORAGE_KEY = 'pp_v2_dashboard_columns';
const ACTIVE_DASHBOARD_KEY = 'pp_v2_dashboard_active';

function loadColumns(): DashboardColumn[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const seeded = parsed.map((c: any) => ({ entityKey: 'operations' as EntityKey, ...c }));
    return migrateColumnsToGrid(seeded);
  } catch {
    return [];
  }
}

/** Lowest free Y for a new tile (placed below all existing rows). */
function nextOpenY(columns: DashboardColumn[]): number {
  if (columns.length === 0) return 0;
  return Math.max(...columns.map((c) => (c.y ?? 0) + (c.h ?? DEFAULT_GRID_H)));
}

/** Set/remove a single column-width override; returns undefined if the result is empty. */
function nextColumnWidths(
  current: Record<string, number> | undefined,
  columnId: string,
  width: number | undefined
): Record<string, number> | undefined {
  const out = { ...(current ?? {}) };
  if (width === undefined) delete out[columnId];
  else out[columnId] = width;
  return Object.keys(out).length === 0 ? undefined : out;
}

function saveColumns(cols: DashboardColumn[]) {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(cols));
}

export function V2Dashboard() {
  // Pull data for every entity so columns can reference any of them.
  const operationsRows = ENTITIES.operations.useData();
  const workOrderRows = ENTITIES['work-orders'].useData();
  const salesOrderRows = ENTITIES['sales-orders'].useData();

  const dataByEntity: Record<EntityKey, any[]> = {
    'operations': operationsRows,
    'work-orders': workOrderRows,
    'sales-orders': salesOrderRows,
  };

  const opsSaved = useSavedViews(ENTITIES.operations.key);
  const woSaved = useSavedViews(ENTITIES['work-orders'].key);
  const soSaved = useSavedViews(ENTITIES['sales-orders'].key);

  const entityViews: EntitySavedViews[] = [
    { entity: ENTITIES.operations, views: opsSaved.views },
    { entity: ENTITIES['work-orders'], views: woSaved.views },
    { entity: ENTITIES['sales-orders'], views: soSaved.views },
  ];

  const findView = (entityKey: EntityKey, viewId: string): SavedView | undefined => {
    switch (entityKey) {
      case 'operations': return opsSaved.views.find((v) => v.id === viewId);
      case 'work-orders': return woSaved.views.find((v) => v.id === viewId);
      case 'sales-orders': return soSaved.views.find((v) => v.id === viewId);
    }
  };

  const {
    views: dashboardViews,
    createView: createDashboard,
    updateView: updateDashboard,
    deleteView: deleteDashboard,
    currentUserId,
  } = useDashboardViews();

  const [columns, setColumns] = useState<DashboardColumn[]>(() => loadColumns());
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACTIVE_DASHBOARD_KEY);
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    saveColumns(columns);
  }, [columns]);

  useEffect(() => {
    if (activeDashboardId) localStorage.setItem(ACTIVE_DASHBOARD_KEY, activeDashboardId);
    else localStorage.removeItem(ACTIVE_DASHBOARD_KEY);
  }, [activeDashboardId]);

  const activeDashboard = dashboardViews.find((d) => d.id === activeDashboardId) ?? null;
  const dirty = activeDashboard
    ? !columnsEqual(columns, activeDashboard.columns)
    : columns.length > 0;

  const addColumn = (pick: ViewPick) => {
    setColumns((prev) => {
      const w = DEFAULT_GRID_W;
      const h = DEFAULT_GRID_H;
      return [
        ...prev,
        {
          id: `col-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          entityKey: pick.entityKey,
          viewId: pick.viewId,
          x: 0,
          y: nextOpenY(prev),
          w,
          h,
        },
      ];
    });
  };

  const [layoutMode, setLayoutMode] = useState(false);

  const setTileRect = (id: string, x: number, y: number, w: number, h: number) => {
    const clampedX = Math.max(0, Math.min(GRID_COLS - 1, Math.round(x)));
    const clampedW = Math.max(1, Math.min(GRID_COLS - clampedX, Math.round(w)));
    const clampedY = Math.max(0, Math.round(y));
    const clampedH = Math.max(2, Math.round(h));
    setColumns((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, x: clampedX, y: clampedY, w: clampedW, h: clampedH } : c
      )
    );
  };

  const removeColumn = (id: string) => {
    setColumns((prev) => prev.filter((c) => c.id !== id));
  };

  const updateColumn = (id: string, patch: Partial<DashboardColumn>) => {
    setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const handleSelectDashboard = (id: string | null) => {
    setActiveDashboardId(id);
    if (id) {
      const dv = dashboardViews.find((d) => d.id === id);
      if (dv) setColumns(dv.columns.map((c) => ({ ...c })));
    }
  };

  const handleSaveDashboard = async (name: string, isShared: boolean) => {
    try {
      const saved = await createDashboard(name, columns, isShared);
      setActiveDashboardId(saved.id);
    } catch (err) {
      console.error('Failed to save dashboard', err);
    }
  };

  const handleUpdateDashboard = async (id: string) => {
    try {
      await updateDashboard(id, { columns });
    } catch (err) {
      console.error('Failed to update dashboard', err);
    }
  };

  const handleDeleteDashboard = async (id: string) => {
    try {
      await deleteDashboard(id);
      if (activeDashboardId === id) setActiveDashboardId(null);
    } catch (err) {
      console.error('Failed to delete dashboard', err);
    }
  };

  const searchedByEntity = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: Record<EntityKey, any[]> = {
      'operations': dataByEntity.operations,
      'work-orders': dataByEntity['work-orders'],
      'sales-orders': dataByEntity['sales-orders'],
    };
    if (!q) return out;
    (Object.keys(out) as EntityKey[]).forEach((key) => {
      const entity = ENTITIES[key];
      out[key] = out[key].filter((row) =>
        entity.columns.some((col) => col.accessor(row).toLowerCase().includes(q))
      );
    });
    return out;
  }, [search, dataByEntity.operations, dataByEntity['work-orders'], dataByEntity['sales-orders']]);

  const handleExport = () =>
    exportDashboardCsv(columns, (e, v) => findView(e, v), searchedByEntity);

  const anyEntityViews = entityViews.some((e) => e.views.length > 0);

  return (
    <div className="px-4 py-3 space-y-3">
      <DashboardToolbar
        search={search}
        onSearchChange={setSearch}
        dashboardViews={dashboardViews}
        activeDashboardViewId={activeDashboardId}
        currentUserId={currentUserId}
        dirty={dirty}
        onSelectDashboard={handleSelectDashboard}
        onSaveDashboard={handleSaveDashboard}
        onUpdateDashboard={handleUpdateDashboard}
        onDeleteDashboard={handleDeleteDashboard}
        entityViews={entityViews}
        onAddColumn={addColumn}
        onExport={handleExport}
        layoutMode={layoutMode}
        onToggleLayout={() => setLayoutMode((v) => !v)}
      />

      {columns.length === 0 ? (
        <EmptyDashboard
          anyViews={anyEntityViews}
          entityViews={entityViews}
          onAdd={addColumn}
        />
      ) : (
        <DashboardGrid
          columns={columns}
          layoutMode={layoutMode}
          onSetRect={setTileRect}
          onRemove={removeColumn}
          renderTile={(col) => {
            const entity = ENTITIES[col.entityKey];
            const view = findView(col.entityKey, col.viewId);
            const title = col.title ?? view?.name ?? '(deleted view)';
            const titleIsOverride = !!col.title;
            const rawRows = searchedByEntity[col.entityKey] ?? [];
            const processed = view
              ? processRows(
                  rawRows,
                  entity.columns,
                  view.state.filters,
                  view.state.sort,
                  view.state.dateFilters
                )
              : [];
            return (
              <ColumnFrame
                title={title}
                color={col.color ?? null}
                viewMode={view?.state.viewMode ?? 'table'}
                editor={
                  <ColumnEditor
                    title={title}
                    titleIsOverride={titleIsOverride}
                    entityKey={col.entityKey}
                    viewId={col.viewId}
                    color={col.color ?? null}
                    isChartView={view?.state.viewMode === 'chart'}
                    chartBg={col.chartBg ?? 'white'}
                    entityViews={entityViews}
                    onChangeView={(pick) =>
                      updateColumn(col.id, {
                        entityKey: pick.entityKey,
                        viewId: pick.viewId,
                      })
                    }
                    onChangeTitle={(t) => updateColumn(col.id, { title: t })}
                    onChangeColor={(c) => updateColumn(col.id, { color: c })}
                    onChangeChartBg={(bg) => updateColumn(col.id, { chartBg: bg })}
                    onDelete={() => removeColumn(col.id)}
                  />
                }
              >
                {view ? (
                  <ColumnRender
                    entity={entity}
                    rows={processed}
                    view={view}
                    columnWidthOverrides={col.columnWidths}
                    chartBg={col.chartBg ?? 'white'}
                    onResizeColumn={(columnId, width) =>
                      updateColumn(col.id, {
                        columnWidths: nextColumnWidths(col.columnWidths, columnId, width),
                      })
                    }
                  />
                ) : (
                  <div className="text-xs text-slate-400 px-3 py-4">
                    The saved view for this column no longer exists.
                  </div>
                )}
              </ColumnFrame>
            );
          }}
        />
      )}
    </div>
  );
}

interface DashboardGridProps {
  columns: DashboardColumn[];
  layoutMode: boolean;
  onSetRect: (id: string, x: number, y: number, w: number, h: number) => void;
  onRemove: (id: string) => void;
  renderTile: (col: DashboardColumn) => React.ReactNode;
}

function DashboardGrid({
  columns,
  layoutMode,
  onSetRect,
  onRemove,
  renderTile,
}: DashboardGridProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [colPx, setColPx] = useState<number>(80);

  useLayoutEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const totalGap = (GRID_COLS - 1) * GRID_GAP_PX;
      const available = el.clientWidth - totalGap;
      setColPx(Math.max(40, available / GRID_COLS));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Total rows is the maximum y+h across all tiles.
  const totalRows = useMemo(() => {
    if (columns.length === 0) return 1;
    return Math.max(...columns.map((c) => (c.y ?? 0) + (c.h ?? DEFAULT_GRID_H)));
  }, [columns]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
        gridAutoRows: `${GRID_ROW_PX}px`,
        gap: `${GRID_GAP_PX}px`,
      }}
      className={cn(
        'relative',
        layoutMode && 'bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[length:calc(100%/12)_64px]'
      )}
    >
      {columns.map((col) => {
        const x = col.x ?? 0;
        const y = col.y ?? 0;
        const w = col.w ?? DEFAULT_GRID_W;
        const h = col.h ?? DEFAULT_GRID_H;
        const style = {
          gridColumn: `${x + 1} / span ${w}`,
          gridRow: `${y + 1} / span ${h}`,
        } as React.CSSProperties;
        if (layoutMode) {
          return (
            <GridTileEditor
              key={col.id}
              style={style}
              title={col.title ?? '(no view)'}
              color={col.color ?? null}
              x={x}
              y={y}
              w={w}
              h={h}
              colPx={colPx}
              onCommit={(nx, ny, nw, nh) => onSetRect(col.id, nx, ny, nw, nh)}
              onRemove={() => onRemove(col.id)}
            />
          );
        }
        return (
          <div key={col.id} style={style} className="min-w-0 overflow-hidden">
            {renderTile(col)}
          </div>
        );
      })}
      {/* Spacer to ensure the grid always has at least totalRows tracks rendered. */}
      <div
        style={{ gridColumn: '1 / -1', gridRow: `${totalRows + 1} / span 1` }}
        className="invisible"
      />
    </div>
  );
}

interface GridTileEditorProps {
  style: React.CSSProperties;
  title: string;
  color: ColorPreset | null;
  x: number;
  y: number;
  w: number;
  h: number;
  colPx: number;
  onCommit: (x: number, y: number, w: number, h: number) => void;
  onRemove: () => void;
}

function GridTileEditor({
  style,
  title,
  color,
  x,
  y,
  w,
  h,
  colPx,
  onCommit,
  onRemove,
}: GridTileEditorProps) {
  const palette = color ? COLOR_PRESETS[color] : null;
  const [live, setLive] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const cur = live ?? { x, y, w, h };

  const colStep = colPx + GRID_GAP_PX;
  const rowStep = GRID_ROW_PX + GRID_GAP_PX;

  const startMove = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { x, y, w, h };
    const onMove = (ev: MouseEvent) => {
      const dx = Math.round((ev.clientX - startX) / colStep);
      const dy = Math.round((ev.clientY - startY) / rowStep);
      setLive({
        x: Math.max(0, Math.min(GRID_COLS - origin.w, origin.x + dx)),
        y: Math.max(0, origin.y + dy),
        w: origin.w,
        h: origin.h,
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setLive((cur) => {
        if (cur) onCommit(cur.x, cur.y, cur.w, cur.h);
        return null;
      });
    };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const startResize = (
    e: React.MouseEvent,
    axis: 'w' | 'h' | 'both'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { x, y, w, h };
    const onMove = (ev: MouseEvent) => {
      const dx = Math.round((ev.clientX - startX) / colStep);
      const dy = Math.round((ev.clientY - startY) / rowStep);
      setLive({
        x: origin.x,
        y: origin.y,
        w: axis === 'h' ? origin.w : Math.max(1, Math.min(GRID_COLS - origin.x, origin.w + dx)),
        h: axis === 'w' ? origin.h : Math.max(2, origin.h + dy),
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setLive((cur) => {
        if (cur) onCommit(cur.x, cur.y, cur.w, cur.h);
        return null;
      });
    };
    document.body.style.cursor =
      axis === 'both' ? 'nwse-resize' : axis === 'w' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // When live preview is active, override the grid placement.
  const finalStyle: React.CSSProperties = live
    ? {
        gridColumn: `${live.x + 1} / span ${live.w}`,
        gridRow: `${live.y + 1} / span ${live.h}`,
        zIndex: 5,
      }
    : style;

  return (
    <div
      style={finalStyle}
      onMouseDown={startMove}
      className={cn(
        'relative rounded-md border-2 border-dashed group flex items-center justify-center cursor-grab active:cursor-grabbing select-none',
        palette
          ? cn(palette.border, palette.bg, 'hover:brightness-95')
          : 'border-slate-300 bg-slate-50/60 hover:border-slate-400'
      )}
    >
      <div className="text-center min-w-0 px-2 pointer-events-none">
        <div
          className={cn(
            'text-xs font-semibold truncate',
            palette ? palette.text : 'text-slate-700'
          )}
          title={title}
        >
          {title}
        </div>
        <div
          className={cn(
            'text-[10px] tabular-nums',
            palette ? palette.text : 'text-slate-400'
          )}
        >
          {cur.w} × {cur.h}
        </div>
      </div>
      <button
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onRemove}
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 p-0.5"
        title="Delete tile"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {/* Right edge */}
      <div
        onMouseDown={(e) => startResize(e, 'w')}
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-blue-300/60"
      />
      {/* Bottom edge */}
      <div
        onMouseDown={(e) => startResize(e, 'h')}
        className="absolute bottom-0 left-0 h-1.5 w-full cursor-row-resize hover:bg-blue-300/60"
      />
      {/* Corner */}
      <div
        onMouseDown={(e) => startResize(e, 'both')}
        className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize bg-slate-300 hover:bg-blue-400 rounded-tl-md"
      />
    </div>
  );
}
interface ColumnFrameProps {
  title: string;
  color: ColorPreset | null;
  viewMode: SavedView['state']['viewMode'];
  editor: React.ReactNode;
  children: React.ReactNode;
}

function ColumnFrame({ title, color, viewMode, editor, children }: ColumnFrameProps) {
  const palette = color ? COLOR_PRESETS[color] : null;
  // Charts must fully fit (no scroll). Calendars handle their own vertical
  // scroll internally — no horizontal scroll. Tables/cards can scroll both
  // ways so dense data stays accessible.
  const innerOverflow =
    viewMode === 'chart'
      ? 'overflow-hidden'
      : viewMode === 'calendar'
        ? 'overflow-hidden'
        : 'overflow-auto';
  return (
    <div
      className={cn(
        'group flex flex-col h-full w-full min-w-0 rounded-md',
        palette && cn('border', palette.border)
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-1 min-h-[1.25rem]',
          palette ? cn('rounded-t-md px-2 py-1', palette.bg, palette.text) : 'mb-1'
        )}
      >
        <span
          className={cn(
            'text-xs font-semibold truncate',
            palette ? palette.text : 'text-slate-700'
          )}
          title={title}
        >
          {title}
        </span>
        {editor}
      </div>
      <div className={cn('flex-1 min-h-0', innerOverflow)}>{children}</div>
    </div>
  );
}

interface ColumnRenderProps {
  entity: EntityConfig;
  rows: any[];
  view: SavedView;
  columnWidthOverrides?: Record<string, number>;
  chartBg?: ChartBackground;
  onResizeColumn?: (columnId: string, width: number | undefined) => void;
}

function effectiveColumnsFor(
  cols: EntityConfig['columns'],
  view: SavedView
): EntityConfig['columns'] {
  const order = view.state.columnOrder;
  const labels = view.state.columnLabels;
  let ordered = cols;
  if (order && order.length > 0) {
    const byId = new Map(cols.map((c) => [c.id, c]));
    const seen = new Set<string>();
    const out: typeof cols = [];
    for (const id of order) {
      const c = byId.get(id);
      if (c && !seen.has(id)) {
        out.push(c);
        seen.add(id);
      }
    }
    for (const c of cols) {
      if (!seen.has(c.id)) out.push(c);
    }
    ordered = out;
  }
  if (!labels) return ordered;
  return ordered.map((c) => (labels[c.id] ? { ...c, label: labels[c.id] } : c));
}

function ColumnRender({
  entity,
  rows,
  view,
  columnWidthOverrides,
  chartBg,
  onResizeColumn,
}: ColumnRenderProps) {
  const cols = effectiveColumnsFor(entity.columns, view);
  if (view.state.viewMode === 'table') {
    const mergedWidths = {
      ...(view.state.columnWidths ?? {}),
      ...(columnWidthOverrides ?? {}),
    };
    return (
      <CompactOperationsTable
        rows={rows}
        columns={cols}
        formats={view.state.formats}
        display={view.state.display}
        sort={view.state.sort}
        totals={view.state.totals}
        columnWidths={Object.keys(mergedWidths).length > 0 ? mergedWidths : undefined}
        onResizeColumn={onResizeColumn}
        groupBy={view.state.groupBy}
        groupMode={view.state.groupMode}
      />
    );
  }
  if (view.state.viewMode === 'calendar') {
    return (
      <CalendarView
        rows={rows}
        columns={cols}
        visibleColumns={cols.filter(
          (c) => !view.state.display.hiddenColumns.has(c.id)
        )}
        config={view.state.calendarConfig}
        titleColumnId={entity.titleColumnId}
      />
    );
  }
  if (view.state.viewMode === 'chart') {
    return (
      <ChartView
        rows={rows}
        columns={cols}
        formats={view.state.formats}
        config={view.state.chartConfig}
        background={chartBg}
      />
    );
  }
  if (rows.length === 0) {
    return <div className="text-xs text-slate-400 px-2 py-4 text-center">No records</div>;
  }
  return (
    <>
      {rows.map((row) => (
        <OperationCard
          key={row.id}
          row={row}
          columns={cols}
          formats={view.state.formats}
          display={view.state.display}
          compact
          titleColumnId={entity.titleColumnId}
          stageAccessor={entity.stageAccessor}
        />
      ))}
    </>
  );
}

interface EmptyDashboardProps {
  anyViews: boolean;
  entityViews: EntitySavedViews[];
  onAdd: (pick: ViewPick) => void;
}

function EmptyDashboard({ anyViews, entityViews, onAdd }: EmptyDashboardProps) {
  const [open, setOpen] = useState(false);

  if (!anyViews) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 border border-dashed border-slate-200 rounded-xl">
        <p className="text-sm font-medium mb-1">No saved views yet</p>
        <p className="text-xs">
          Save a view in Operations, Work Orders, or Sales Orders, then add it as a column.
        </p>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="w-full h-40 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600 transition-colors">
          <Plus className="w-5 h-5" />
          <span className="text-sm font-medium">Add your first column</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-1">
        <div className="py-1 max-h-80 overflow-y-auto">
          {entityViews.map(({ entity, views }) =>
            views.length === 0 ? null : (
              <div key={entity.key} className="mb-1">
                <div className="px-3 pt-1 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400">
                  {entity.label}
                </div>
                {views.map((v) => (
                  <button
                    key={v.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm hover:bg-slate-50 text-left"
                    onClick={() => {
                      onAdd({ entityKey: entity.key, viewId: v.id });
                      setOpen(false);
                    }}
                  >
                    <Bookmark className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-slate-900 truncate">{v.name}</span>
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function exportDashboardCsv(
  columns: DashboardColumn[],
  findView: (entity: EntityKey, viewId: string) => SavedView | undefined,
  searchedByEntity: Record<EntityKey, any[]>
) {
  const escape = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const allColIds = new Set<string>();
  const lines: string[] = [];

  // Compute a unified header across all entities so CSV can combine rows.
  ENTITY_LIST.forEach((e) => e.columns.forEach((c) => allColIds.add(`${e.key}:${c.id}`)));
  const headerCells = ['Column', 'Entity'];
  ENTITY_LIST.forEach((e) =>
    e.columns.forEach((c) => headerCells.push(`${e.label} · ${c.label}`))
  );
  lines.push(headerCells.map(escape).join(','));

  for (const col of columns) {
    const entity = ENTITIES[col.entityKey];
    const view = findView(col.entityKey, col.viewId);
    if (!view) continue;
    const title = col.title ?? view.name;
    const rawRows = searchedByEntity[col.entityKey] ?? [];
    const rows = processRows(rawRows, entity.columns, view.state.filters, view.state.sort);
    for (const r of rows) {
      const cells = [escape(title), escape(entity.label)];
      for (const e of ENTITY_LIST) {
        for (const c of e.columns) {
          cells.push(e.key === col.entityKey ? escape(c.accessor(r)) : '""');
        }
      }
      lines.push(cells.join(','));
    }
  }

  const csv = lines.join('\n') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
